import { ModelBase } from "../abstract/model-base.model";
import { AddressFormat } from "./address-format.model";
import { Civility } from "../user/civility.model";
import { Country } from "../core/country.model";
import { PersonNameOrderEnum } from "../../enums/person-name-order.enum";

export class Address extends ModelBase {

    static override CLASS_NAME: string = 'Address';

    private _civility!: Civility;
    private _firstName!: string;
    private _lastName!: string;
    private _secondCivility!: Civility;
    private _secondFirstName!: string;
    private _secondLastName!: string;
    private _company!: string;
    private _service!: string;
    private _cedexCode!: string;
    private _cedexLabel!: string;
    private _appartment!: string;
    private _building!: string;
    private _format!: AddressFormat;
    private _street1!: string;
    private _street2!: string;
    private _zipCode!: string;
    private _cityName!: string;
    private _stateProvince!: string;
    private _country!: Country;
    private _placeId!: string;

    public copy(source: Address): void {
        this.civility = source.civility;
        this.firstName = source.firstName;
        this.lastName = source.lastName;
        this.secondCivility = source.secondCivility;
        this.secondFirstName = source.secondFirstName;
        this.secondLastName = source.secondLastName;
        this.company = source.company;
        this.service = source.service;
        this.cedexCode = source.cedexCode;
        this.cedexLabel = source.cedexLabel;
        this.appartment = source.appartment;
        this.building = source.building;
        this.format = source.format;
        this.street1 = source.street1;
        this.street2 = source.street2;
        this.zipCode = source.zipCode;
        this.cityName = source.cityName;
        this.stateProvince = source.stateProvince;
        this.country = source.country;
        this.placeId = source.placeId;
    }

    public get isComplete(): boolean {
        if (this._format?.code === 'individual') {
            return !!this._firstName && !!this._lastName;
        }
        return !!this._company && !!this._street1
            && ((!!this._zipCode && !!this._cityName) || (!!this._cedexCode && !!this._cedexLabel))
            && !!this._country;
    }

    /**
     * @param withName  inclure la ou les personnes (particulier) ou la société en tête
     * @param nameOrder ordre du nom et du prénom d'un particulier — réglage `addressNameOrder`
     *                  du dossier (#1088), « Prénom Nom » à défaut. Doit suivre la même
     *                  règle que le PDF rendu par le serveur.
     */
    public toReadableFormat(withName: boolean = true, nameOrder?: string): string | undefined {
        let address = '';

        if (withName) {
            // Si c'est l'adresse d'un particulier
            if (this._format.code === 'individual') {
                const person = this._formatPerson(this._civility, this._firstName, this._lastName, nameOrder);
                if (person.length > 0) {
                    address += `${person}\n`;
                }
                // S'il y a une deuxième personne, dans le même ordre
                const secondPerson = this._formatPerson(this._secondCivility, this._secondFirstName, this._secondLastName, nameOrder);
                if (secondPerson.length > 0) {
                    address += `${secondPerson}\n`;
                }
            } else {
                if (this._company && this._company.length > 0) {
                    address += `${this._company}`;
                }
                if (this._service && this._service.length > 0) {
                    address += `, service ${this._service}\n`;
                } else if (address.length > 0) {
                    address += `\n`;
                }
            }
        }

        // Données complémentaires
        if (this._street1 && this._street1.length > 0) {
            address += `${this._street1}\n`;
        }
        if (this._street2 && this._street2.length > 0) {
            address += `${this._street2}\n`;
        }
        if (this._building && this._building.length > 0) {
            address += `Bâtiment ${this._building} Apt ${this._appartment}\n`;
        } else if (this._appartment && this._appartment.length > 0) {
            address += `Appartement ${this._appartment}\n`;
        }

        // Ville
        if (this._cedexCode && this._cedexCode.length > 0) {
            address += `${this._cedexCode} ${this._cedexLabel}\n`;
        } else {
            if (this._zipCode && this._zipCode.length > 0) {
                address += `${this._zipCode} `;
            }
            const cityDisplay = this._cityName;
            if (cityDisplay) {
                address += `${cityDisplay.toUpperCase()}\n`;
            } else if (address.length > 0) {
                address += `\n`;
            }
        }

        // Région
        if (this._stateProvince && this._stateProvince.length > 0) {
            address += `${this._stateProvince}\n`;
        }

        // Pays
        const countryDisplay = this._country?.name;
        if (countryDisplay) {
            address += `${countryDisplay}`;
        }

        if (address.length === 0) {
            return undefined;
        }
        return address;
    }

    /**
     * Ligne d'une personne du bloc adresse : civilité devant, puis prénom et nom dans l'ordre
     * demandé. Une information absente ne laisse ni séparateur ni espace en trop.
     */
    private _formatPerson(civility: Civility, firstName: string, lastName: string, nameOrder?: string): string {
        const names = nameOrder === PersonNameOrderEnum.LastFirst ? [lastName, firstName] : [firstName, lastName];
        return [civility?.label, ...names].filter(part => !!part).join(' ');
    }

    public get civility(): Civility {
        return this._civility;
    }

    public set civility(value: any) {
        this._setValue('_civility', value, 'Civility');
    }

    public get firstName(): string {
        return this._firstName;
    }

    public set firstName(value: any) {
        this._setValue('_firstName', value, 'string');
    }

    public get lastName(): string {
        return this._lastName;
    }

    public set lastName(value: any) {
        this._setValue('_lastName', value, 'string');
    }

    public get secondCivility(): Civility {
        return this._secondCivility;
    }

    public set secondCivility(value: any) {
        this._setValue('_secondCivility', value, 'Civility');
    }

    public get secondFirstName(): string {
        return this._secondFirstName;
    }

    public set secondFirstName(value: any) {
        this._setValue('_secondFirstName', value, 'string');
    }

    public get secondLastName(): string {
        return this._secondLastName;
    }

    public set secondLastName(value: any) {
        this._setValue('_secondLastName', value, 'string');
    }

    public get company(): string {
        return this._company;
    }

    public set company(value: any) {
        this._setValue('_company', value, 'string');
    }

    public get service(): string {
        return this._service;
    }

    public set service(value: any) {
        this._setValue('_service', value, 'string');
    }

    public get cedexCode(): string {
        return this._cedexCode;
    }

    public set cedexCode(value: any) {
        this._setValue('_cedexCode', value, 'string');
    }

    public get cedexLabel(): string {
        return this._cedexLabel;
    }

    public set cedexLabel(value: any) {
        this._setValue('_cedexLabel', value, 'string');
    }

    public get appartment(): string {
        return this._appartment;
    }

    public set appartment(value: any) {
        this._setValue('_appartment', value, 'string');
    }

    public get building(): string {
        return this._building;
    }

    public set building(value: any) {
        this._setValue('_building', value, 'string');
    }

    public get format(): AddressFormat {
        return this._format;
    }

    public set format(value: any) {
        this._setValue('_format', value, 'AddressFormat');
    }

    public get street1(): string {
        return this._street1;
    }

    public set street1(value: any) {
        this._setValue('_street1', value, 'string');
    }

    public get street2(): string {
        return this._street2;
    }

    public set street2(value: any) {
        this._setValue('_street2', value, 'string');
    }

    public get zipCode(): string {
        return this._zipCode;
    }

    public set zipCode(value: any) {
        this._setValue('_zipCode', value, 'string');
    }

    public get cityName(): string {
        return this._cityName;
    }

    public set cityName(value: any) {
        this._setValue('_cityName', value, 'string');
    }

    public get stateProvince(): string {
        return this._stateProvince;
    }

    public set stateProvince(value: any) {
        this._setValue('_stateProvince', value, 'string');
    }

    public get country(): Country {
        return this._country;
    }

    public set country(value: any) {
        this._setValue('_country', value, 'Country');
    }

    public get placeId(): string {
        return this._placeId;
    }

    public set placeId(value: any) {
        this._setValue('_placeId', value, 'string');
    }

}